import { DiffNodeConfig, DiffNodeRenderType } from './node-config';
import { intentInfoToServer } from '@interface/intent';
import { compositeInfoToServer } from '@interface/composite';
import { entityInfoToServer, entityContentToServer } from '@interface/entity';
import { valueSuggestionToServer } from '@interface/suggestion';
import { dataQueryToServer } from '@interface/data-query';
import { valueAssociationToServer } from '@interface/value-association';
import { valueCheckToServer } from '@interface/value-check';
import { dialogActToServer } from '@interface/dialog-act';
import { connectionToServer } from '@interface/connection';
import { confirmationInfoToServer } from '@interface/confirmation';
import { attributeCompanionToServer } from '@interface/companion';
import { responseToServer } from '@interface/response';
import { attributeToServer, filteringToServer, slotInitializationToServer } from '@interface/parameter';
import { agentToServer } from '@interface/agent';
import { PAGE_TYPE } from '@config/index';
import { trainingPhraseToServer } from '@interface/training-phrase';
import { promptInfoToServer } from '@interface/prompt';
import { serviceInfoToServer } from '@interface/service';
import { testCaseToServer } from '@interface/try-it-now';
import { stateUpdateInfoToServer } from '@interface/state-update';
import { frameMappingToServer } from '@interface/frame-mapping';
import { genericTypeToServer } from '@interface/generic-type';
import { externalTypeToServer } from '@interface/external-type';
import { Action } from 'history';
import { slotStorageAnnotationToServer } from '@interface/storage-annotation';

export const PhraseNode: DiffNodeConfig = {
  page: PAGE_TYPE.PHRASE,
  funcToServer: trainingPhraseToServer,
  fields: [
    {
      key: 'trainingPhrase',
      localeNameKey: 'common_text',
      hasMasterSelf: true,
      isLeaf: true
    }
  ]
};

export const PromptNode: DiffNodeConfig = {
  page: PAGE_TYPE.PROMPT,
  funcToServer: promptInfoToServer,
  fields: [
    {
      key: 'prompt',
      localeNameKey: 'common_text',
      hasMasterSelf: true,
      isLeaf: true
    }
  ]
};

export const AttributeCompanionNode: DiffNodeConfig = {
  funcToServer: attributeCompanionToServer,
  page: PAGE_TYPE.ATTRIBUTE,
  fields: [
    {
      key: 'name',
      localeNameKey: 'attribute_name'
    },
    {
      key: 'multiValue',
      localeNameKey: 'attribute_multi_value',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'expressions',
      name: 'Expressions',
      isArray: true,
      node: PhraseNode
    }
  ]
};


const ResponseActionNode: DiffNodeConfig = {
  fields: [
    {
      key: 'type',
      name: 'Type',
      renderType: DiffNodeRenderType.responseActionType
    },
    {
      key: 'templateId',
      name: 'Message Template',
      renderType: DiffNodeRenderType.messageTemplate
    },
    {
      key: 'content',
      name: 'Content'
    },
    {
      key: 'sourceType',
      localeNameKey: 'source_type'
    },
    {
      key: 'source',
      localeNameKey: 'list_reply_closure'
    },
    {
      key: 'variable',
      name: 'Variable',
      renderType: DiffNodeRenderType.variable
    },
    {
      key: 'parameters',
      localeNameKey: 'response_type_external_parameters'
    },
    {
      key: 'itemSize',
      localeNameKey: 'common_instance_count'
    },
    {
      key: 'header',
      name: 'Header'
    },
    {
      key: 'body',
      name: 'Body'
    },
    {
      key: 'footer',
      name: 'Footer'
    },
    {
      key: 'source',
      name: 'Source'
    },
    {
      key: 'delimiterField',
      localeNameKey: 'list_reply_delimiter_field'
    },
    {
      key: 'delimiterEnd',
      localeNameKey: 'list_reply_delimiter_end'
    },
    {
      key: 'values',
      name: 'Value',
      isArray: true
    },
    {
      key: 'multiValue',
      localeNameKey: 'attribute_multi_value'
    },
    {
      key: 'paramAction',
      name: 'Attribute Action',
      renderType: DiffNodeRenderType.paramAction
    },
    {
      key: 'intent',
      localeNameKey: 'response_type_intent_action',
      renderType: DiffNodeRenderType.frame
    },
    {
      key: 'name',
      localeNameKey: 'response_type_external_action_name'
    },
    {
      key: 'url',
      localeNameKey: 'media_reply_url'
    },
    {
      key: 'description',
      localeNameKey: 'media_reply_description'
    },
    {
      key: 'otherChannelData',
      name: 'Other Channel Data'
    }
  ]
};

export const ResponseChannelNode: DiffNodeConfig = {
  fields: [
    {
      key: 'tag',
      name: 'Tag'
    },
    {
      key: 'actions',
      name: 'Actions',
      // node: ResponseActionNode,
      isArray: true
    }
  ]
};

export const ValueSuggestionNode: DiffNodeConfig = {
  localeNameKey: 'value_suggestion',
  funcToServer: valueSuggestionToServer,
  page: PAGE_TYPE.SUGGESTION,
  fields: [
    {
      key: 'vrSourceType',
      localeNameKey: 'source_type',
      hasMasterSelf: true
    },
    {
      key: 'source',
      name: 'Source',
      hasMasterSelf: true
    },
    {
      key: 'sourceComment',
      name: 'Source Comment',
      hasMasterSelf: true
    },
    {
      key: 'sourceIntent',
      localeNameKey: 'vr_source',
      hasMasterSelf: true
    },
    {
      key: 'channels',
      name: 'Channels',
      isArray: true,
      hasMasterSelf: true
    },
    {
      key: 'soft',
      localeNameKey: 'value_rec_hard',
      hasMasterSelf: true,
      isLeaf: true,
      renderType: DiffNodeRenderType.valueRecHard
    },
    {
      key: 'singlePrompts',
      localeNameKey: 'value_suggestion_single_prompts',
      isArray: true,
      node: PromptNode
    },
    {
      key: 'singleAffirmatives',
      localeNameKey: 'value_rec_single_affirmatives',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'singleNegatives',
      localeNameKey: 'value_rec_single_negatives',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'zeroPrompts',
      localeNameKey: 'value_suggestion_zero_prompts',
      isArray: true,
      node: PromptNode
    }
  ]
};

export const ConfirmationNode: DiffNodeConfig = {
  page: PAGE_TYPE.ATTRIBUTE,
  funcToServer: confirmationInfoToServer,
  fields: [
    {
      key: 'codeCondition',
      localeNameKey: 'condition_type_code_expression',
      isLeaf: true,
      hasMasterSelf: true
    },
    {
      key: 'codeComment',
      name: 'Comment',
      hasMasterSelf: true
    },
    {
      key: 'explicitConfirmation',
      localeNameKey: 'value_rec_explicit_confirmation',
      hasMasterSelf: true,
      isLeaf: true,
      renderType: DiffNodeRenderType.boolean
    },
    {
      key: 'messageType',
      localeNameKey: 'confirmation_message_type',
      hasMasterSelf: true,
      isLeaf: true
    },
    {
      key: 'prompts',
      localeNameKey: 'attribute_prompts',
      isArray: true,
      node: PromptNode
    },
    {
      key: 'affirmatives',
      localeNameKey: 'attribute_expression_yes',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'negatives',
      localeNameKey: 'attribute_expression_no',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'listAction',
      localeNameKey: 'display',
      hasMasterSelf: true
    }
  ]
};

export const ValueCheckNode: DiffNodeConfig = {
  funcToServer: valueCheckToServer,
  page: PAGE_TYPE.CHECK,
  fields: [
    {
      key: 'source',
      name: 'Source',
      hasMasterSelf: true
    },
    {
      key: 'sourceComment',
      name: 'Source Comment',
      hasMasterSelf: true,
    },
    {
      key: 'variable',
      name: 'Variable',
      renderType: DiffNodeRenderType.variable,
      hasMasterSelf: true
    },
    {
      key: 'parameters',
      localeNameKey: 'input_parameters',
      hasMasterSelf: true
    },
    {
      key: 'prompts',
      name: 'prompts',
      isArray: true,
      node: PromptNode
    },
    {
      key: 'recovering',
      localeNameKey: 'value_check_recovering',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'recoveringActions',
      name: 'Actions',
      isArray: true,
      hasMasterSelf: true
    }
  ]
};

export const DialogActNode: DiffNodeConfig = {
  funcToServer: dialogActToServer,
  page: PAGE_TYPE.DIALOG_ACT,
  fields: [
    {
      key: 'intent',
      name: 'Dialog Act Intent',
      renderType: DiffNodeRenderType.variable,
      hasMasterSelf: true
    },
    {
      key: 'prompts',
      name: 'prompts',
      isArray: true,
      node: PromptNode
    }
  ]
};

export const PostFillActionNode: DiffNodeConfig = {
  funcToServer: dialogActToServer,
  page: PAGE_TYPE.POST_FILL_ACTION,
  fields: [
    {
      key: 'condition',
      localeNameKey: 'common_condition',
      hasMasterSelf: true
    },
    {
      key: 'conditionComment',
      name: 'Comment',
      hasMasterSelf: true
    },
    {
      key: 'actions',
      name: 'Actions',
      isArray: true,
      hasMasterSelf: true
    }
  ]
};

export const SlotInitializationNode: DiffNodeConfig = {
  funcToServer: slotInitializationToServer,
  page: PAGE_TYPE.INITIALIZATION,
  fields: [
    {
      key: 'target',
      name: 'Dialog Act Intent',
      renderType: DiffNodeRenderType.variable,
      hasMasterSelf: true
    },
    {
      key: 'association',
      name: 'Association',
      hasMasterSelf: true
    },
    {
      key: 'associationComment',
      name: 'Association Comment',
      hasMasterSelf: true
    }
  ]
};

export const FilteringNode: DiffNodeConfig = {
  funcToServer: filteringToServer,
  page: PAGE_TYPE.INITIALIZATION,
  fields: [
    {
      key: 'sourceSlot',
      name: 'Source slot',
      renderType: DiffNodeRenderType.variable,
      hasMasterSelf: true
    },
    {
      key: 'filteringExpression',
      name: 'Filtering expression',
      hasMasterSelf: true
    },
    {
      key: 'informPrompts',
      name: 'Inform Prompts',
      isArray: true,
      node: PromptNode
    }
  ]
};

export const AttributeNode: DiffNodeConfig = {
  name: 'Slot',
  funcToServer: attributeToServer,
  page: PAGE_TYPE.ATTRIBUTE,
  fields: [
    {
      key: 'name',
      localeNameKey: 'attribute_name',
      hasMasterSelf: true
    },
    {
      key: 'comment',
      localeNameKey: 'comment',
      hasMasterSelf: true
    },
    {
      key: 'proto',
      localeNameKey: 'attribute_proto_name',
      renderType: DiffNodeRenderType.frame
    },
    {
      key: 'customizationPath',
      name: 'Customization slot path',
      renderType: DiffNodeRenderType.variable
    },
    {
      key: 'implementation',
      localeNameKey: 'implementation',
      renderType: DiffNodeRenderType.frame
    },
    {
      key: 'description',
      localeNameKey: 'common_description',
      hasMasterSelf: true
    },
    {
      key: 'triggers',
      localeNameKey: 'triggers',
      isArray: true,
      hasMasterSelf: true
    },
    {
      key: 'multiValue',
      localeNameKey: 'attribute_multi_value',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'auxiliary',
      localeNameKey: 'auxiliary',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    // {
    //   key: 'allowMultiValuePrompt',
    //   localeNameKey: 'attribute_allow_multi_value_prompt',
    //   renderType: DiffNodeRenderType.boolean,
    //   hasMasterSelf: true
    // },
    {
      key: 'multiValueMin',
      localeNameKey: 'attribute_multi_value_min',
      hasMasterSelf: true
    },
    {
      key: 'multiValueMax',
      localeNameKey: 'attribute_multi_value_max',
      hasMasterSelf: true
    },
    {
      key: 'allowDontCare',
      localeNameKey: 'attribute_allow_dont_care',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    // {
    //   key: 'allowPrompt',
    //   localeNameKey: 'attribute_allow_prompt',
    //   renderType: DiffNodeRenderType.boolean,
    //   hasMasterSelf: true
    // },
    {
      key: 'promptQuestions',
      localeNameKey: 'attribute_prompts_diff', // 当key输出为'Prompt'时无法赋值到对象中,其他任何key都正常，所以在key中加了空格
      name: 'prompts',
      isArray: true,
      node: PromptNode
    },
    {
      key: 'allowExpression',
      localeNameKey: 'attribute_allow_expression',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'promptExpressions',
      localeNameKey: 'attribute_prompt_expressions',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'enableValueExpression',
      localeNameKey: 'attribute_enable_value_expression',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'valueExpressions',
      localeNameKey: 'attribute_value_expressions',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'fillMode',
      name: 'Fill mode',
      isLeaf: true,
      hasMasterSelf: true
    },
    {
      key: 'messagingType',
      name: 'Messaging type',
      isLeaf: true,
      hasMasterSelf: true
    },
    {
      key: 'askStrategy',
      localeNameKey: 'attribute_ask_strategy',
      isLeaf: true,
      hasMasterSelf: true
    },
    {
      key: 'fillWithNaturalLanguage',
      name: 'Fill with natural language',
      isLeaf: true,
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'booleanGated',
      name: 'Boolean gated',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'gatedType',
      name: 'Gated type',
      renderType: DiffNodeRenderType.variable,
      hasMasterSelf: true
    },
    {
      key: 'confirmations',
      localeNameKey: 'annotation_confirmation',
      isArray: true,
      node: ConfirmationNode,
      isLeaf: true
    },
    {
      key: 'multiValueQuestions',
      isArray: true,
      localeNameKey: 'attribute_multi_value_prompts',
      node: PromptNode
    },
    {
      key: 'multiValuedConfirmations',
      localeNameKey: 'annotation_multi_valued_confirmation',
      isArray: true,
      node: ConfirmationNode,
      isLeaf: true
    },
    {
      key: 'multiValueExpressionsNo',
      localeNameKey: 'attribute_multi_value_expressions_no',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'multiValueMinExceptions',
      isArray: true,
      localeNameKey: 'attribute_multi_value_min_exceptions',
      node: PromptNode
    },
    {
      key: 'multiValueMaxExceptions',
      isArray: true,
      localeNameKey: 'attribute_multi_value_max_exceptions',
      node: PromptNode
    },
    {
      key: 'dontCareExpressions',
      localeNameKey: 'attribute_dont_care_expressions',
      isArray: true,
      node: PhraseNode
    },
    // {
    //   key: 'allowCompanion',
    //   name: 'Companion enabled',
    //   renderType: DiffNodeRenderType.boolean,
    //   hasMasterSelf: true
    // },
    {
      key: 'parameterCompanions',
      localeNameKey: 'attribute_companions',
      isArray: true,
      node: AttributeCompanionNode,
      hasMasterSelf: true
    },
    // {
    //   key: 'askConditionType',
    //   localeNameKey: 'condition_type',
    //   isLeaf: true,
    //   hasMasterSelf: true
    // },
    // {
    //   key: 'askConditions',
    //   localeNameKey: 'attribute_ask_condition',
    //   isArray: true,
    //   renderType: DiffNodeRenderType.triggerCondition,
    //   hasMasterSelf: true
    // },
    {
      key: 'askCodeCondition',
      localeNameKey: 'condition_type_code_expression',
      isLeaf: true,
      hasMasterSelf: true
    },
    {
      key: 'conditionComment',
      localeNameKey: 'condition_type_code_comment',
      isLeaf: true,
      hasMasterSelf: true
    },
    {
      key: 'informPrompts',
      name: 'Inform Prompts',
      isArray: true,
      node: PromptNode
    },
    {
      key: 'booleanGatePrompts',
      localeNameKey: 'ask_strategy_boolean_gate',
      isArray: true,
      hasMasterSelf: true
    },
    {
      key: 'booleanGateExpressionsYes',
      localeNameKey: 'attribute_boolean_gate_expressions_yes',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'booleanGateExpressionsNo',
      localeNameKey: 'attribute_boolean_gate_expressions_no',
      isArray: true,
      node: PhraseNode
    },
    // {
    //   key: 'allowSlotUpdate',
    //   localeNameKey: 'attribute_allow_slot_update',
    //   renderType: DiffNodeRenderType.boolean,
    //   hasMasterSelf: true
    // },
    {
      key: 'slotUpdateExpressions',
      name: 'Slot update literal',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'negativeExpressions',
      localeNameKey: 'attribute_negative_expressions',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'entailmentExpressions',
      name: 'Slot update implied',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'slotAppendExpressions',
      name: 'Slot append',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'slotDeleteExpressions',
      name: 'Slot delete',
      isArray: true,
      node: PhraseNode
    },

    {
      key: 'abortPhrases',
      name: 'Abort intent literal expressions',
      isArray: true,
      node: PhraseNode
    },

    {
      key: 'abortEntailmentPhrases',
      name: 'Abort intent implied expressions',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'abortNegativePhrases',
      name: 'Abort intent negative expressions',
      isArray: true,
      node: PhraseNode
    },
    // {
    //   key: 'allowValueSuggestion',
    //   localeNameKey: 'attribute_allow_value_suggestion',
    //   renderType: DiffNodeRenderType.boolean,
    //   hasMasterSelf: true
    // },
    {
      key: 'valueSuggestions',
      localeNameKey: 'value_suggestion',
      isArray: true,
      node: ValueSuggestionNode
    },
    // {
    //   key: 'allowValueCheck',
    //   localeNameKey: 'attribute_allow_value_check',
    //   renderType: DiffNodeRenderType.boolean,
    //   hasMasterSelf: true
    // },
    {
      key: 'valueChecks',
      localeNameKey: 'value_check',
      isArray: true,
      node: ValueCheckNode
    },
    // {
    //   key: 'allowInitialization',
    //   localeNameKey: 'slot_allow_initialization',
    //   renderType: DiffNodeRenderType.boolean,
    //   hasMasterSelf: true
    // },
    {
      key: 'initializations',
      localeNameKey: 'slot_initialization',
      isArray: true,
      node: SlotInitializationNode
    },
    {
      key: 'enableFiltering',
      name: 'Enable filtering',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'filterings',
      name: 'Filterings',
      isArray: true,
      node: FilteringNode
    },
    {
      key: 'allowDialogAct',
      localeNameKey: 'attribute_allow_dialog_act',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'dialogActs',
      localeNameKey: 'dialog_acts',
      isArray: true,
      node: DialogActNode
    },
    // {
    //   key: 'allowPostFillAction',
    //   localeNameKey: 'post_fill_action',
    //   renderType: DiffNodeRenderType.boolean,
    //   hasMasterSelf: true
    // },
    {
      key: 'postFillActions',
      localeNameKey: 'post_fill_actions',
      isArray: true,
      node: PostFillActionNode
    },
    {
      key: 'storageAnnotation',
      localeNameKey: 'storage_annotation'
      // node: SlotStorageAnnotationNode
    }
  ]
};

const DatabaseNode: DiffNodeConfig = {
  localeNameKey: 'agent_tab_database_config',
  fields: [
    {
      key: 'query',
      localeNameKey: 'database_code'
    },
    {
      key: 'functionMeta',
      localeNameKey: 'data_query_function_meta'
    },
    {
      key: 'cron',
      name: 'Cron'
    }
  ]
};

const RestfulNode: DiffNodeConfig = {
  name: 'Webhook',
  fields: [
    {
      key: 'method',
      name: 'Method'
    },
    {
      key: 'url',
      name: 'URL'
    },
    {
      key: 'postBody',
      name: 'Post body'
    },
    {
      key: 'headers',
      name: 'Headers'
    }
  ]
};

const PsqlNode: DiffNodeConfig = {
  name: 'Psql',
  fields: [
    {
      key: 'query',
      name: 'Query'
    },
    {
      key: 'resultFormat',
      name: 'Result Format'
    }
  ]
};

const DataQueryInputTypeNode: DiffNodeConfig = {
  fields: [
    {
      key: 'name',
      localeNameKey: 'common_name'
    },
    {
      key: 'type',
      name: 'Parameter',
      renderType: DiffNodeRenderType.frame
    },
    {
      key: 'multiValue',
      localeNameKey: 'attribute_multi_value',
      renderType: DiffNodeRenderType.boolean
    },
    {
      key: 'nullable',
      localeNameKey: 'data_query_nullable',
      renderType: DiffNodeRenderType.boolean
    },
    {
      key: 'comment',
      localeNameKey: 'comment'
    }
  ]
};

const NativeFunctionNode: DiffNodeConfig = {
  name: 'Native',
  fields: [
    {
      key: 'parameters',
      localeNameKey: 'data_query_input_type',
      node: DataQueryInputTypeNode,
      isArray: true,
      hasMasterSelf: true
    },
    {
      key: 'body',
      name: 'Body'
    }
  ]
};

export const DataQueryNode: DiffNodeConfig = {
  localeNameKey: 'data_query',
  funcToServer: dataQueryToServer,
  page: PAGE_TYPE.CLOSURE,
  fields: [
    {
      key: 'name',
      localeNameKey: 'data_query_name',
      hasMasterSelf: true
    },
    {
      key: 'comment',
      localeNameKey: 'comment',
      hasMasterSelf: true
    },
    {
      key: 'abstract',
      localeNameKey: 'common_abstract',
      hasMasterSelf: true,
      renderType: DiffNodeRenderType.boolean
    },
    {
      key: 'parameters',
      localeNameKey: 'data_query_input_type',
      node: DataQueryInputTypeNode,
      isArray: true,
      hasMasterSelf: true
    },
    {
      key: 'queryType',
      localeNameKey: 'data_query_type',
      hasMasterSelf: true,
      renderType: DiffNodeRenderType.dataQueryType
    },
    {
      key: 'kotlin',
      name: 'Kotlin',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'schedule',
      name: 'Schedule',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'databaseConfig',
      localeNameKey: 'agent_tab_database_config',
      node: DatabaseNode,
      hasMasterSelf: true
    },
    {
      key: 'restfulConfig',
      name: 'Webhook',
      node: RestfulNode,
      hasMasterSelf: true
    },
    {
      key: 'functionConfig',
      name: 'Native Config',
      node: NativeFunctionNode,
      hasMasterSelf: true
    },
    {
      key: 'promptImplemented',
      name: 'Prompt Implemented',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'promptConfig',
      name: 'Prompt Config',
      hasMasterSelf: true
    },
    {
      key: 'bodyComment',
      name: 'Body Comment',
      hasMasterSelf: true
    },
    {
      key: 'frame',
      localeNameKey: 'data_query_frame',
      renderType: DiffNodeRenderType.frame,
      hasMasterSelf: true
    },
    {
      key: 'multiValue',
      localeNameKey: 'attribute_multi_value',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'disableReturnNull',
      localeNameKey: 'data_query_disable_null',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'errorMsgs',
      localeNameKey: 'common_error_info',
      hasMasterSelf: true
    },
    {
      key: 'allowTrigger',
      localeNameKey: 'data_query_allow_trigger',
      hasMasterSelf: true,
      renderType: DiffNodeRenderType.boolean
    },
    {
      key: 'sinkSlot',
      localeNameKey: 'data_query_sink_slot',
      hasMasterSelf: true,
      renderType: DiffNodeRenderType.frame
    },
    {
      key: 'triggerExpressions',
      localeNameKey: 'data_query_trigger_expressions',
      hasMasterSelf: true,
      isArray: true,
      node: PhraseNode
    }
  ]
};

export const AssociationNode: DiffNodeConfig = {
  idKey: 'variable',
  name: 'Associations',
  funcToServer: valueAssociationToServer,
  fields: [
    {
      key: 'variable',
      localeNameKey: 'association_target',
      renderType: DiffNodeRenderType.variable
    },
    {
      key: 'defaultValues',
      localeNameKey: 'association_default_value',
      renderType: DiffNodeRenderType.variable,
      isArray: true
    }
  ]
};

// const ConnectionDbNode: DiffNodeConfig = {
//   name: 'Database Config',
//   fields: [
//     {
//       key: 'type',
//       localeNameKey: 'db_type'
//     },
//     {
//       key: 'dbName',
//       localeNameKey: 'db_name'
//     },
//     {
//       key: 'dbHost',
//       localeNameKey: 'db_host'
//     },
//     {
//       key: 'dbPort',
//       localeNameKey: 'db_port'
//     },
//     {
//       key: 'dialect',
//       localeNameKey: 'db_dialect'
//     },
//     {
//       key: 'userName',
//       localeNameKey: 'account_user_name'
//     }
//   ]
// };

export const ConnectionNode: DiffNodeConfig = {
  name: 'Connections',
  nameKey: 'name',
  funcToServer: connectionToServer,
  page: PAGE_TYPE.CONNECTION,
  fields: [
    {
      key: 'name',
      name: 'Label',
      hasMasterSelf: true
    },
    {
      key: 'interfaceService',
      localeNameKey: 'service_interface',
      hasMasterSelf: true
    },
    {
      key: 'implementationService',
      localeNameKey: 'service_implementation',
      hasMasterSelf: true
    },
    {
      key: 'configuration',
      name: 'Configuration',
      hasMasterSelf: true
    }
  ]
};

export const EntityContentNode: DiffNodeConfig = {
  nameKey: 'label',
  funcToServer: entityContentToServer,
  page: PAGE_TYPE.ENTITY_CONTENT,
  fields: [
    {
      key: 'appendTo',
      name: 'Appended to',
      hasMasterSelf: true
    },
    {
      key: 'label',
      localeNameKey: 'entity_content_label',
      hasMasterSelf: true
    },
    {
      key: 'expressions',
      localeNameKey: 'entity_content_expressions',
      isArray: true,
      hasMasterSelf: true
    }
  ]
};

export const EntityContextNode: DiffNodeConfig = {
  idKey: 'id',
  fields: [
    {
      key: 'name',
      localeNameKey: 'common_name'
    },
    {
      key: 'type',
      localeNameKey: 'common_type'
    }
  ]
};

export const EntityNode: DiffNodeConfig = {
  funcToServer: entityInfoToServer,
  page: PAGE_TYPE.ENTITY,
  fields: [
    {
      key: 'name',
      localeNameKey: 'common_name',
      hasMasterSelf: true
    },
    {
      key: 'comment',
      localeNameKey: 'comment',
      hasMasterSelf: true
    },
    {
      key: 'source',
      name: 'Source',
      renderType: DiffNodeRenderType.source
    },
    {
      key: 'abstract',
      localeNameKey: 'common_abstract',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'cuiDisabled',
      name: 'CUI Disabled',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'normalizable',
      localeNameKey: 'common_normalizable',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'content',
      localeNameKey: 'entity_content',
      isArray: true,
      node: EntityContentNode
    },
    {
      key: 'synonym',
      localeNameKey: 'entity_alias',
      isArray: true,
      hasMasterSelf: true
    },
    {
      key: 'parents',
      localeNameKey: 'entity_contained',
      node: EntityContextNode,
      isArray: true,
      hasMasterSelf: true
    },
    {
      key: 'description',
      localeNameKey: 'common_description',
      hasMasterSelf: true
    },
    {
      key: 'qualification',
      localeNameKey: 'qualification',
      hasMasterSelf: true
    },
    {
      key: 'externalQualification',
      localeNameKey: 'common_external',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'sourceType',
      name: 'Source type',
      hasMasterSelf: true
    },
    {
      key: 'dynamic',
      localeNameKey: 'entity_dynamic',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'dynamicSource',
      localeNameKey: 'dynamic_source',
      hasMasterSelf: true
    },
    {
      key: 'dynamicComment',
      localeNameKey: 'dynamic_comment',
      hasMasterSelf: true
    },
    {
      key: 'recognizer',
      localeNameKey: 'entity_recognizer',
      hasMasterSelf: true,
      isArray: true
    },
    {
      key: 'dontCareExpressions',
      localeNameKey: 'dont_care_expressions',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'pronounExpressions',
      localeNameKey: 'pronoun_expressions',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'dataQueries',
      localeNameKey: 'data_query',
      isArray: true,
      node: DataQueryNode
    }
  ]
};

export const ResponseNode: DiffNodeConfig = {
  funcToServer: responseToServer,
  page: PAGE_TYPE.RESPONSE,
  fields: [
    {
      key: 'name',
      localeNameKey: 'common_response_name',
      hasMasterSelf: true
    },
    // {
    //   key: 'conditionType',
    //   localeNameKey: 'condition_type',
    //   isLeaf: true,
    //   hasMasterSelf: true
    // },
    // {
    //   key: 'triggerConditions',
    //   localeNameKey: 'common_condition',
    //   isArray: true,
    //   renderType: DiffNodeRenderType.triggerCondition,
    //   hasMasterSelf: true
    // },
    {
      key: 'codeCondition',
      localeNameKey: 'condition_type_code_expression',
      isLeaf: true,
      hasMasterSelf: true
    },
    {
      key: 'codeComment',
      name: 'Code Comment',
      hasMasterSelf: true
    },
    {
      key: 'channels',
      name: 'Channels',
      isArray: true,
      node: ResponseChannelNode,
      hasMasterSelf: true
    }
  ]
};

export const InheritNode: DiffNodeConfig = {
  idKey: 'id',
  fields: [
    {
      key: 'name',
      localeNameKey: 'common_name'
    },
    {
      key: 'type',
      localeNameKey: 'common_type'
    }
  ]
};

export const CronNode: DiffNodeConfig = {
  fields: [
    {
      key: 'dataQueryId',
      name: 'Contact',
      renderType: DiffNodeRenderType.dataQueryName
    },
    {
      key: 'schedule',
      name: 'Schedule'
    }
  ]
};

export const GenericTypeNode: DiffNodeConfig = {
  localeNameKey: 'generic_type',
  funcToServer: genericTypeToServer,
  fields: [
    {
      key: 'name',
      localeNameKey: 'name'
    }
  ]
};

export const ExternalTypeNode: DiffNodeConfig = {
  localeNameKey: 'external_type',
  funcToServer: externalTypeToServer,
  fields: [
    {
      key: 'name',
      localeNameKey: 'name'
    }
  ]
};

export const StateUpdateActionNode: DiffNodeConfig = {
  localeNameKey: 'state_update_actions',
  fields: [
    {
      key: 'strategy',
      localeNameKey: 'state_update_strategy'
    },
    {
      key: 'value',
      localeNameKey: 'state_update_strategy_value',
      hasMasterSelf: true
    },
    {
      key: 'otherSlot',
      localeNameKey: 'state_update_strategy_other_slot',
      renderType: DiffNodeRenderType.variable
    },
    {
      key: 'function',
      localeNameKey: 'state_update_strategy_function_call',
      renderType: DiffNodeRenderType.variable
    },
    {
      key: 'parameters',
      localeNameKey: 'input_parameters'
    },
    {
      key: 'codeExpression',
      localeNameKey: 'state_update_strategy_code_expression'
    }
  ]
};

export const StateUpdateEventStateNode: DiffNodeConfig = {
  localeNameKey: 'state_update_event_state',
  fields: [
    {
      key: 'targetSlot',
      localeNameKey: 'state_update_target',
      renderType: DiffNodeRenderType.variable
    },
    {
      key: 'codeExpression',
      localeNameKey: 'state_update_strategy_code_expression'
    }
  ]
};

export const StateUpdateRuleNode: DiffNodeConfig = {
  localeNameKey: 'state_update_rule',
  fields: [
    {
      key: 'targetSlot',
      localeNameKey: 'state_update_target',
      renderType: DiffNodeRenderType.variable
    },
    {
      key: 'method',
      localeNameKey: 'state_update_rule_method'
    },
    {
      key: 'codeExpressions',
      localeNameKey: 'state_update_strategy_code_expression'
    }
  ]
};


export const StateUpdateNode: DiffNodeConfig = {
  localeNameKey: 'state_update',
  funcToServer: stateUpdateInfoToServer,
  fields: [
    {
      key: 'name',
      name: 'Name',
      hasMasterSelf: true
    },
    {
      key: 'triggerMethod',
      localeNameKey: 'state_update_trigger_method',
      hasMasterSelf: true
    },
    {
      key: 'event',
      localeNameKey: 'state_update_event',
      renderType: DiffNodeRenderType.variable,
      hasMasterSelf: true
    },
    {
      key: 'eventStates',
      localeNameKey: 'state_update_event_state',
      node: StateUpdateEventStateNode,
      isArray: true,
      hasMasterSelf: true
    },
    // {
    //   key: 'conditions',
    //   localeNameKey: 'common_condition',
    //   isArray: true,
    //   renderType: DiffNodeRenderType.triggerCondition,
    //   hasMasterSelf: true
    // },
    // {
    //   key: 'conditionType',
    //   localeNameKey: 'condition_type',
    //   isLeaf: true,
    //   hasMasterSelf: true
    // },
    {
      key: 'conditionCode',
      localeNameKey: 'condition_type_code_expression',
      isLeaf: true,
      hasMasterSelf: true
    },
    {
      key: 'codeComment',
      name: 'Code Comment',
      hasMasterSelf: true
    },
    {
      key: 'triggerTiming',
      localeNameKey: 'state_update_trigger_timing',
      renderType: DiffNodeRenderType.variable,
      hasMasterSelf: true
    },
    {
      key: 'channels',
      name: 'Channels',
      isArray: true,
      node: ResponseChannelNode,
      hasMasterSelf: true
    }
  ]
};


export const FrameMappingContentNode: DiffNodeConfig = {
  localeNameKey: 'frame_mapping_contents',
  fields: [
    {
      key: 'targetSlot',
      localeNameKey: 'frame_mapping_target_slot'
    },
    {
      key: 'mappingSlot',
      localeNameKey: 'frame_mapping_mapping_slot'
    },
    {
      key: 'conversionMode',
      localeNameKey: 'frame_mapping_conversion_mode'
    }
  ]
};

export const FrameMappingNode: DiffNodeConfig = {
  localeNameKey: 'frame_mapping',
  funcToServer: frameMappingToServer,
  fields: [
    {
      key: 'targetFrame',
      localeNameKey: 'frame_mapping_target_frame',
      hasMasterSelf: true
    },
    {
      key: 'contents',
      localeNameKey: 'frame_mapping_contents',
      node: FrameMappingContentNode,
      isArray: true,
      hasMasterSelf: true
    }
  ]
};

export const IntentNode: DiffNodeConfig = {
  idKey: 'id',
  page: PAGE_TYPE.INTENT,
  funcToServer: intentInfoToServer,
  fields: [
    {
      key: 'name',
      localeNameKey: 'common_name',
      hasMasterSelf: true
    },
    {
      key: 'comment',
      localeNameKey: 'comment',
      hasMasterSelf: true
    },
    {
      key: 'source',
      name: 'Source',
      renderType: DiffNodeRenderType.source
    },
    {
      key: 'description',
      localeNameKey: 'common_description',
      hasMasterSelf: true
    },
    {
      key: 'triggers',
      localeNameKey: 'triggers',
      isArray: true,
      hasMasterSelf: true
    },
    {
      key: 'dialogAct',
      localeNameKey: 'dialog_act',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'trainingPhrases',
      localeNameKey: 'training_phrase',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'entailmentPhrases',
      localeNameKey: 'entailment_expressions',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'negativePhrases',
      localeNameKey: 'common_negative_expressions',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'abortPhrases',
      name: 'Abort intent literal expressions',
      isArray: true,
      node: PhraseNode
    },

    {
      key: 'abortEntailmentPhrases',
      name: 'Abort intent implied expressions',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'abortNegativePhrases',
      name: 'Abort intent negative expressions',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'attributes',
      localeNameKey: 'attribute_list',
      isArray: true,
      node: AttributeNode
    },
    {
      key: 'slotPosition',
      name: 'Slot Order',
      isLeaf: true,
      hasMasterSelf: true,
      renderType: DiffNodeRenderType.pageOrder
    },
    {
      key: 'nestedSlotCustomizations',
      localeNameKey: 'nested_slot_customization',
      isArray: true,
      node: AttributeNode
    },
    {
      key: 'valueSuggestions',
      localeNameKey: 'value_suggestion',
      isArray: true,
      node: ValueSuggestionNode
    },
    {
      key: 'dataQueries',
      localeNameKey: 'data_query',
      isArray: true,
      node: DataQueryNode
    },
    {
      key: 'services',
      localeNameKey: 'service',
      isArray: true,
      hasMasterSelf: true
    },
    {
      key: 'returnInfo',
      localeNameKey: 'intent_return',
      hasMasterSelf: true
    },
    {
      key: 'branching',
      name: 'Branching',
      renderType: DiffNodeRenderType.boolean
    },
    {
      key: 'responses',
      localeNameKey: 'common_response',
      isArray: true,
      node: ResponseNode
    },
    {
      key: 'responsePosition',
      name: 'Response Order',
      isLeaf: true,
      hasMasterSelf: true,
      renderType: DiffNodeRenderType.pageOrder
    },
    {
      key: 'inheritedFrames',
      localeNameKey: 'inherit',
      isArray: true,
      node: InheritNode
    },
    // {
    //   key: 'genericTypes',
    //   localeNameKey: 'generic_type',
    //   isLeaf: true,
    //   isArray: true,
    //   hasMasterSelf: true
    // },
    // {
    //   key: 'externalTypes',
    //   localeNameKey: 'external_type',
    //   isLeaf: true,
    //   isArray: true,
    //   hasMasterSelf: true
    // },
    {
      key: 'postFillActions',
      localeNameKey: 'post_fill_actions',
      isArray: true,
      node: PostFillActionNode
    },
    {
      key: 'stateUpdates',
      localeNameKey: 'state_update',
      isArray: true,
      node: StateUpdateNode
    },
    {
      key: 'mappings',
      localeNameKey: 'frame_mapping',
      isArray: true,
      node: FrameMappingNode
    },
    {
      key: 'confirmations',
      localeNameKey: 'annotation_confirmation',
      isArray: true,
      node: ConfirmationNode,
      isLeaf: true
    },
    {
      key: 'dialogActs',
      localeNameKey: 'dialog_acts',
      isArray: true,
      node: DialogActNode
    },
    {
      key: 'cronData',
      name: 'Cron',
      node: CronNode,
      hasMasterSelf: true
    },
    {
      key: 'useExpression',
      localeNameKey: 'use_expression',
      hasMasterSelf: true
    },
    {
      key: 'qualification',
      localeNameKey: 'qualification',
      hasMasterSelf: true
    },
    {
      key: 'externalQualification',
      localeNameKey: 'common_external',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'triggeredByDU',
      name: 'Triggered by dialog understanding',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'booleanGatePrompts',
      localeNameKey: 'ask_strategy_boolean_gate',
      isArray: true,
      hasMasterSelf: true
    },
    {
      key: 'booleanGateExpressionsYes',
      localeNameKey: 'attribute_boolean_gate_expressions_yes',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'booleanGateExpressionsNo',
      localeNameKey: 'attribute_boolean_gate_expressions_no',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'enableFallback',
      name: 'Enable Fallback',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'fallbackInfo',
      name: 'Fallback Info',
      hasMasterSelf: true
    },
    {
      key: 'soft',
      name: 'Prompt engineered',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'softResponse',
      name: 'Soft response',
      hasMasterSelf: true
    }
  ]
};

export const CompositeNode: DiffNodeConfig = {
  idKey: 'id',
  page: PAGE_TYPE.COMPOSITE,
  funcToServer: compositeInfoToServer,
  fields: [
    {
      key: 'name',
      localeNameKey: 'common_name',
      hasMasterSelf: true
    },
    {
      key: 'comment',
      localeNameKey: 'comment',
      hasMasterSelf: true
    },
    {
      key: 'source',
      name: 'Source',
      renderType: DiffNodeRenderType.source
    },
    {
      key: 'description',
      localeNameKey: 'common_description',
      hasMasterSelf: true
    },
    {
      key: 'isInterface',
      localeNameKey: 'is_interface',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'cuiDisabled',
      name: 'CUI Disabled',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'triggers',
      localeNameKey: 'triggers',
      isArray: true,
      hasMasterSelf: true
    },
    {
      key: 'trainingPhrases',
      localeNameKey: 'training_phrase',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'negativePhrases',
      localeNameKey: 'common_negative_expressions',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'dontCareExpressions',
      localeNameKey: 'dont_care_expressions',
      isArray: true,
      node: PhraseNode,
      renderType: DiffNodeRenderType.trainingPhrase,
      isLeaf: true
    },
    {
      key: 'inheritedFrames',
      localeNameKey: 'inherit',
      isArray: true,
      node: InheritNode
    },
    {
      key: 'attributes',
      localeNameKey: 'attribute_list',
      isArray: true,
      node: AttributeNode
    },
    {
      key: 'nestedSlotCustomizations',
      localeNameKey: 'nested_slot_customization',
      isArray: true,
      node: AttributeNode
    },
    {
      key: 'slotPosition',
      name: 'Slot Order',
      isLeaf: true,
      hasMasterSelf: true,
      renderType: DiffNodeRenderType.pageOrder
    },
    {
      key: 'headSlot',
      localeNameKey: 'head_slot',
      hasMasterSelf: true
    },
    {
      key: 'dataQueries',
      localeNameKey: 'data_query',
      isArray: true,
      node: DataQueryNode
    },
    {
      key: 'services',
      localeNameKey: 'service',
      isArray: true,
      hasMasterSelf: true
    },
    {
      key: 'valueSuggestions',
      localeNameKey: 'value_suggestion',
      isArray: true,
      node: ValueSuggestionNode
    },
    // {
    //   key: 'genericTypes',
    //   localeNameKey: 'generic_type',
    //   isLeaf: true,
    //   isArray: true,
    //   hasMasterSelf: true
    // },
    // {
    //   key: 'externalTypes',
    //   localeNameKey: 'external_type',
    //   isLeaf: true,
    //   isArray: true,
    //   hasMasterSelf: true
    // },
    {
      key: 'associations',
      localeNameKey: 'association',
      isArray: true,
      isLeaf: true,
      node: AssociationNode
    },
    {
      key: 'postFillActions',
      localeNameKey: 'post_fill_actions',
      isArray: true,
      node: PostFillActionNode
    },
    {
      key: 'stateUpdates',
      localeNameKey: 'state_update',
      isArray: true,
      node: StateUpdateNode
    },
    {
      key: 'mappings',
      localeNameKey: 'frame_mapping',
      isArray: true,
      node: FrameMappingNode
    },
    {
      key: 'valueChecks',
      localeNameKey: 'value_check',
      isArray: true,
      node: ValueCheckNode
    },
    {
      key: 'confirmations',
      localeNameKey: 'annotation_confirmation',
      isArray: true,
      node: ConfirmationNode,
      isLeaf: true
    },
    {
      key: 'dialogActs',
      localeNameKey: 'dialog_acts',
      isArray: true,
      node: DialogActNode
    },
    {
      key: 'qualification',
      localeNameKey: 'qualification',
      hasMasterSelf: true
    },
    {
      key: 'externalQualification',
      localeNameKey: 'common_external',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'sourceType',
      name: 'Source type',
      hasMasterSelf: true
    },
    {
      key: 'weak',
      localeNameKey: 'weak_type',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'associateType',
      localeNameKey: 'associate_type',
      hasMasterSelf: true
    },
    {
      key: 'useExpression',
      localeNameKey: 'use_expression',
      hasMasterSelf: true
    },
    {
      key: 'isGlobal',
      localeNameKey: 'global',
      hasMasterSelf: true,
      renderType: DiffNodeRenderType.boolean
    },
    {
      key: 'storageEnable',
      localeNameKey: 'storage_enabled',
      hasMasterSelf: true,
      renderType: DiffNodeRenderType.boolean
    },
    {
      key: 'storageAnnotation',
      localeNameKey: 'storage_annotation'
    },
    {
      key: 'returnInfo',
      localeNameKey: 'intent_return',
      hasMasterSelf: true
    },
    {
      key: 'booleanGatePrompts',
      localeNameKey: 'ask_strategy_boolean_gate',
      isArray: true,
      hasMasterSelf: true
    },
    {
      key: 'booleanGateExpressionsYes',
      localeNameKey: 'attribute_boolean_gate_expressions_yes',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'booleanGateExpressionsNo',
      localeNameKey: 'attribute_boolean_gate_expressions_no',
      isArray: true,
      node: PhraseNode
    },
    {
      key: 'enableFallback',
      name: 'Enable Fallback',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'fallbackInfo',
      name: 'Fallback Info',
      hasMasterSelf: true
    }
  ]
};

export const serviceNode: DiffNodeConfig = {
  idKey: 'id',
  page: PAGE_TYPE.SERVICE,
  funcToServer: serviceInfoToServer,
  fields: [
    {
      key: 'name',
      localeNameKey: 'common_name',
      hasMasterSelf: true
    },
    {
      key: 'comment',
      localeNameKey: 'comment',
      hasMasterSelf: true
    },
    {
      key: 'source',
      name: 'Source',
      renderType: DiffNodeRenderType.source
    },
    {
      key: 'description',
      localeNameKey: 'common_description',
      hasMasterSelf: true
    },
    {
      key: 'psql',
      name: 'psql',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'isInterface',
      localeNameKey: 'is_interface',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'inheritedFrames',
      localeNameKey: 'inherit',
      isArray: true,
      node: InheritNode
    },
    {
      key: 'attributes',
      localeNameKey: 'attribute_list',
      isArray: true,
      node: AttributeNode
    },
    {
      key: 'slotPosition',
      name: 'Slot Order',
      isLeaf: true,
      hasMasterSelf: true,
      renderType: DiffNodeRenderType.pageOrder
    },
    {
      key: 'nestedSlotCustomizations',
      localeNameKey: 'nested_slot_customization',
      isArray: true,
      node: AttributeNode
    },
    {
      key: 'dataQueries',
      localeNameKey: 'data_query',
      isArray: true,
      node: DataQueryNode
    },
    {
      key: 'services',
      localeNameKey: 'service',
      isArray: true,
      hasMasterSelf: true
    },
    {
      key: 'qualification',
      localeNameKey: 'qualification',
      hasMasterSelf: true
    },
    {
      key: 'externalQualification',
      localeNameKey: 'common_external',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'providerType',
      name: 'Provider Type',
      hasMasterSelf: true
    },
    {
      key: 'sqlConfig',
      name: 'SQL Config',
      hasMasterSelf: true
    },
    {
      key: 'webhookConfig',
      name: 'Webhook Config',
      hasMasterSelf: true
    },
    {
      key: 'googleSheetsConfig',
      name: 'Google Sheets Config',
      hasMasterSelf: true
    },
    {
      key: 'nativeConfig',
      name: 'Native Provider Config',
      hasMasterSelf: true
      // },
      // {
      //   key: 'externalTypes',
      //   localeNameKey: 'external_type',
      //   isArray: true,
      //   isLeaf: true,
      //   node: ExternalTypeNode
    }
  ]
};


export const TestCaseNode: DiffNodeConfig = {
  page: PAGE_TYPE.TEST_CASE,
  funcToServer: testCaseToServer,
  fields: [
    {
      key: 'name',
      localeNameKey: 'common_name',
      hasMasterSelf: true
    },
    {
      key: 'turns',
      name: 'Turns',
      hasMasterSelf: true
    }
  ]
};

export const AgentDataNode: DiffNodeConfig = {
  page: PAGE_TYPE.AGENT,
  funcToServer: agentToServer,
  fields: [
    {
      key: 'name',
      localeNameKey: 'common_name',
      hasMasterSelf: true
    },
    {
      key: 'description',
      localeNameKey: 'common_description',
      hasMasterSelf: true
    },
    {
      key: 'iconUrl',
      name: 'Icon',
      hasMasterSelf: true
    },
    {
      key: 'privacy',
      name: 'Privacy',
      hasMasterSelf: true
    },
    {
      key: 'deployMode',
      name: 'Deploy Mode',
      hasMasterSelf: true
    },
    {
      key: 'timeZone',
      localeNameKey: 'time_zone',
      hasMasterSelf: true
    },
    {
      key: 'prApprovalRequired',
      localeNameKey: 'pr_approval_required',
      renderType: DiffNodeRenderType.boolean,
      hasMasterSelf: true
    },
    {
      key: 'importedAgents',
      name: 'Imported Agents',
      isArray: true,
      hasMasterSelf: true,
      renderType: DiffNodeRenderType.library
    },
    {
      key: 'intents',
      localeNameKey: 'common_intent',
      isArray: true,
      node: IntentNode
    },
    {
      key: 'composites',
      localeNameKey: 'common_semantic_structure',
      isArray: true,
      node: CompositeNode
    },
    {
      key: 'entities',
      localeNameKey: 'common_primitive',
      isArray: true,
      node: EntityNode
    },
    {
      key: 'services',
      localeNameKey: 'sidebar_service',
      isArray: true,
      node: serviceNode
    },
    {
      key: 'connections',
      localeNameKey: 'agent_tab_database_config',
      isArray: true,
      node: ConnectionNode
    },
    {
      key: 'testCases',
      localeNameKey: 'test_case',
      isArray: true,
      node: TestCaseNode
    },
    {
      key: 'readMe',
      name: 'Read Me',
      hasMasterSelf: true
    }
  ]
};
